import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StepIndicator   from './StepIndicator';
import CalendarPreview from './CalendarPreview';
import Footer          from './Footer';
import DayColumnViz    from './DayColumnViz';
import {
  CATEGORIES, TIMEZONES, generateRows, generateTimes,
  formatTime, isBusinessHour,
} from '../lib/constants';
import { createPoll } from '../lib/supabase';

function GalacticTitle() {
  const navigate = useNavigate();
  return (
    <h1 style={{ marginBottom: 10, display: 'inline-block' }}>
      <button
        onClick={() => navigate('/')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', padding: 0, color: 'inherit' }}
        aria-label="Galactic Meeting — return to home"
      >
        <span style={{ color: 'var(--accent)' }}>Galactic</span> Meeting
      </button>
    </h1>
  );
}

export default function OrganizerFlow() {
  const navigate = useNavigate();

  const [step, setStep]                   = useState(0);
  const [title, setTitle]                 = useState('');
  const [instructions, setInstructions]   = useState('');
  const [timezone, setTimezone]           = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  );
  const [granularity, setGranularity]     = useState(60);
  const [meetingDuration, setMeetingDuration] = useState(60);
  const [category, setCategory]           = useState('');
  const [selectedSlots, setSelectedSlots] = useState({});
  const [hoveredRow, setHoveredRow]       = useState(null);
  const [calMode, setCalMode]             = useState('abstract');
  const [calYear, setCalYear]             = useState(new Date().getFullYear());
  const [calMonth, setCalMonth]           = useState(new Date().getMonth());
  const [use12h, setUse12h]              = useState(true);
  const [bizOnly, setBizOnly]            = useState(true);
  const [saving, setSaving]               = useState(false);

  const rows  = useMemo(() => category ? generateRows(category) : [], [category]);
  const times = useMemo(() => generateTimes(granularity), [granularity]);

  // Weekend days: 0=Sun, 6=Sat
  const isWeekendRow = row => row.days && row.days.every(d => d === 0 || d === 6);
  const hasWeekendOnly = row => row.days && row.days.length > 0 && row.days.every(d => d === 0 || d === 6);

  const visibleRows  = useMemo(() =>
    bizOnly ? rows.filter(r => !hasWeekendOnly(r)) : rows,
  [rows, bizOnly]);

  const visibleTimes = useMemo(() =>
    bizOnly ? times.filter(t => isBusinessHour(t)) : times,
  [times, bizOnly]);

  const selectedRows = useMemo(() => {
    const ids = new Set(Object.keys(selectedSlots).map(k => k.split('|')[0]));
    return rows.filter(r => ids.has(r.id));
  }, [selectedSlots, rows]);

  const slotCount = Object.keys(selectedSlots).length;

  const canNext = [
    title.trim().length > 0,
    category !== '',
    slotCount > 0,
  ];

  const toggleSlot = (rowId, time) => {
    setSelectedSlots(prev => {
      const key  = rowId + '|' + time;
      const next = Object.assign({}, prev);
      if (next[key]) delete next[key]; else next[key] = true;
      return next;
    });
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const rulesJson = Object.keys(selectedSlots).map(k => {
        const [rowId, time] = k.split('|');
        return { rowId, time };
      });
      const poll = await createPoll({
        title, instructions, category,
        timezone, granularity,
        duration: meetingDuration,
        rules_json: rulesJson,
        _rows: rows,
      });
      navigate('/share/' + poll.id);
    } finally {
      setSaving(false);
    }
  };

  // Sidebar shared between steps 2+
  const CalSidebar = ({ bizOnly: sidebarBizOnly = false }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Calendar preview card */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Calendar preview</span>
          <div className="toggle-wrap" role="group" aria-label="Calendar view mode">
            <button className={'toggle-opt' + (calMode === 'abstract' ? ' active' : '')}
              onClick={() => setCalMode('abstract')}
              aria-pressed={calMode === 'abstract'}
              style={{ fontSize: 11, padding: '4px 10px' }}>Abstract</button>
            <button className={'toggle-opt' + (calMode === 'real' ? ' active' : '')}
              onClick={() => setCalMode('real')}
              aria-pressed={calMode === 'real'}
              style={{ fontSize: 11, padding: '4px 10px' }}>Real</button>
          </div>
        </div>

        {calMode === 'real' && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={prevMonth} aria-label="Previous month">←</button>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              {new Date(calYear, calMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}
            </span>
            <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={nextMonth} aria-label="Next month">→</button>
          </div>
        )}

        <CalendarPreview
          selectedRows={selectedRows}
          hoveredRow={hoveredRow}
          mode={calMode}
          year={calYear}
          month={calMonth}
          dimWeekends={sidebarBizOnly}
        />
        <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10, textAlign: 'center' }}>
          Hover options to preview · selected in purple
        </p>
      </div>

      {/* Day column visualization */}
      {hoveredRow && (
        <DayColumnViz
          row={hoveredRow}
          selectedTimes={Object.keys(selectedSlots)
            .filter(k => k.startsWith(hoveredRow.id + '|'))
            .map(k => k.split('|')[1])}
          duration={meetingDuration}
          use12h={use12h}
          voterData={null}
        />
      )}

      {/* Poll summary */}
      <div className="card">
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Poll summary</div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{title || 'Untitled'}</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
          {timezone} · {granularity}-min slots · {meetingDuration}-min meetings
        </div>
        <div style={{ fontSize: 13, color: 'var(--accent)' }}>
          {slotCount} option{slotCount !== 1 ? 's' : ''} selected
        </div>
      </div>
    </div>
  );

  return (
    <main id="main-content" style={{ maxWidth: 960, margin: '0 auto', padding: '48px 24px', position: 'relative', zIndex: 1 }}>
      <div style={{ textAlign: 'center', marginBottom: 52 }}>
        <GalacticTitle />
        <p style={{ color: 'var(--muted)', fontSize: 15 }}>
          Date-independent recurring meeting polls for teams distributed across the galaxy
        </p>
      </div>

      <StepIndicator step={step} total={3} />

      {/* ── Step 0: Basics ── */}
      {step === 0 && (
        <div className="card fade-up" style={{ maxWidth: 540, margin: '0 auto' }}>
          <h2 style={{ marginBottom: 24 }}>Create a poll</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>Meeting name <span aria-hidden="true">*</span></span>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Planetary Alignment Meeting"
                aria-required="true"
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>Description/instructions (optional)</span>
              <textarea
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                rows={3}
                placeholder="Any context for participants…"
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>Your timezone</span>
              <select value={timezone} onChange={e => setTimezone(e.target.value)}>
                {!TIMEZONES.find(tz => tz.value === timezone) && (
                  <option value={timezone}>{timezone}</option>
                )}
                {TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </label>

            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }} id="gran-label">Slot granularity</div>
                <div className="toggle-wrap" role="group" aria-labelledby="gran-label">
                  <button className={'toggle-opt' + (granularity === 60 ? ' active' : '')} onClick={() => setGranularity(60)} aria-pressed={granularity === 60}>60 min</button>
                  <button className={'toggle-opt' + (granularity === 30 ? ' active' : '')} onClick={() => setGranularity(30)} aria-pressed={granularity === 30}>30 min</button>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>Meeting length</span>
                  <select
                    value={meetingDuration}
                    onChange={e => setMeetingDuration(Number(e.target.value))}
                    style={{ width: 'auto', fontSize: 13, padding: '6px 10px' }}
                  >
                    {[15, 30, 45, 60, 90, 120, 180].map(d => (
                      <option key={d} value={d}>{d < 60 ? d + ' min' : (d / 60) + (d === 60 ? ' hour' : ' hours')}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <button className="btn-primary" disabled={!canNext[0]} onClick={() => setStep(1)} style={{ marginTop: 4 }}>
              Next: Choose recurrence →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 1: Category ── */}
      {step === 1 && (
        <div className="card fade-up" style={{ maxWidth: 580, margin: '0 auto' }}>
          <h2 style={{ marginBottom: 6 }}>Recurrence pattern</h2>
          <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 24 }}>
            Choose one category — this shapes the poll options.
          </p>
          <div role="radiogroup" aria-label="Recurrence pattern" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
            {CATEGORIES.map(c => (
              <button
                key={c.id}
                role="radio"
                aria-checked={category === c.id}
                onClick={() => setCategory(c.id)}
                style={{
                  padding: '16px 20px', borderRadius: 12, cursor: 'pointer',
                  border: '1px solid ' + (category === c.id ? 'var(--accent)' : 'var(--border2)'),
                  background: category === c.id ? 'rgba(124,111,247,0.07)' : 'var(--surface2)',
                  transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 16,
                  textAlign: 'left', width: '100%', font: 'inherit',
                }}
              >
                <div aria-hidden="true" style={{
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                  border: '2px solid ' + (category === c.id ? 'var(--accent)' : 'var(--border2)'),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {category === c.id && (
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--accent)' }} />
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 500, marginBottom: 2 }}>{c.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.desc}</div>
                </div>
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn-ghost" onClick={() => setStep(0)}>← Back</button>
            <button className="btn-primary" style={{ flex: 1 }} disabled={!canNext[1]} onClick={() => setStep(2)}>
              Next: Pick time slots →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Slot picker ── */}
      {step === 2 && (
        <div className="fade-up poll-layout">
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <h2>Select time slots</h2>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{slotCount} selected</span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
              Click a time to add it as a poll option. Hover to preview on the calendar.
            </p>

            {/* Controls: time format + business hours filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
              <span id="timefmt-label" style={{ fontSize: 12, color: 'var(--muted)' }}>Time format:</span>
              <div className="toggle-wrap" role="group" aria-labelledby="timefmt-label">
                <button className={'toggle-opt' + (use12h ? ' active' : '')}
                  onClick={() => setUse12h(true)} aria-pressed={use12h} style={{ fontSize: 11, padding: '4px 10px' }}>12h</button>
                <button className={'toggle-opt' + (!use12h ? ' active' : '')}
                  onClick={() => setUse12h(false)} aria-pressed={!use12h} style={{ fontSize: 11, padding: '4px 10px' }}>24h</button>
              </div>
              <span id="bizhours-label" style={{ fontSize: 12, color: 'var(--muted)' }}>Business hours only:</span>
              <div className="toggle-wrap" role="group" aria-labelledby="bizhours-label">
                <button className={'toggle-opt' + (bizOnly ? ' active' : '')}
                  onClick={() => setBizOnly(true)} aria-pressed={bizOnly} style={{ fontSize: 11, padding: '4px 10px' }}>On</button>
                <button className={'toggle-opt' + (!bizOnly ? ' active' : '')}
                  onClick={() => setBizOnly(false)} aria-pressed={!bizOnly} style={{ fontSize: 11, padding: '4px 10px' }}>Off</button>
              </div>
              {!bizOnly && (
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                  — <span style={{ color: 'var(--text)' }}>brighter</span> times are typical work hours
                </span>
              )}
            </div>

            <div style={{ maxHeight: 520, overflowY: 'auto', paddingRight: 6 }}>
              {visibleRows.map(row => {
                const rowActive = Object.keys(selectedSlots).some(k => k.startsWith(row.id + '|'));
                const isHovered = hoveredRow && hoveredRow.id === row.id;
                return (
                  <div
                    key={row.id}
                    onMouseEnter={() => setHoveredRow(row)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      marginBottom: 4, padding: '10px 14px', borderRadius: 10,
                      border: '1px solid ' + (rowActive ? 'var(--accent)' : 'var(--border)'),
                      background: rowActive ? 'rgba(124,111,247,0.04)' : isHovered ? 'rgba(255,255,255,0.015)' : 'transparent',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ fontSize: 12, color: rowActive ? 'var(--accent)' : 'var(--muted)', marginBottom: 8, fontWeight: 500 }}>
                      {row.label}
                    </div>
                    <div role="group" aria-label={row.label + ' time slots'} style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {visibleTimes.map(t => {
                        const key   = row.id + '|' + t;
                        const isSel = !!selectedSlots[key];
                        const isBiz = isBusinessHour(t);
                        return (
                          <button
                            key={t}
                            className={'time-block' + (isSel ? ' selected' : '')}
                            onClick={() => toggleSlot(row.id, t)}
                            aria-pressed={isSel}
                            aria-label={formatTime(t, use12h) + (isSel ? ', selected' : '')}
                            style={!isSel && !bizOnly ? { color: isBiz ? 'var(--text)' : 'var(--muted)' } : {}}
                          >
                            {formatTime(t, use12h)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button className="btn-ghost" onClick={() => setStep(1)}>← Back</button>
              <button
                className="btn-primary"
                style={{ flex: 1 }}
                disabled={!canNext[2] || saving}
                onClick={handleCreate}
              >
                {saving ? 'Creating…' : 'Create poll →'}
              </button>
            </div>
          </div>

          <div className="poll-sidebar">
            <CalSidebar bizOnly={bizOnly} />
          </div>
        </div>
      )}

      <Footer showDonate />
    </main>
  );
}
